import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, firstValueFrom, take } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { Comment, CommentsFacade, CreateCommentDTO, InitialMentionsService } from "@webapp/comments";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { KpiProjection } from "@webapp/kpis/models/kpi-projections.models";
import { KpiSnapshot } from "@webapp/kpis/models/kpi-snapshot.models";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { NoteWithGifAndMentions } from "@webapp/shared/rich-text-editor-with-mentions/rich-text-editor-with-mentions.component";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { CHART_POINT_DATE_FORMAT } from "../../kpi-details/kpi-chart/utils";
import { KpiCommentTargetType } from "../models";
import { shouldShowInfoIcon } from "../utils";

export type KpiCommentFormModalData = {
  kpi: Kpi;
  snapshots: KpiSnapshot[];
  projections: KpiProjection[];
  comments: Comment[];
  onCommentCreated(kpiId: string): void;
};

type KpiCommentForm = {
  targetType: FormControl<KpiCommentTargetType>;
  comment: FormControl<NoteWithGifAndMentions>;
  targetDate: FormControl<Date>;
};

@UntilDestroy()
@Component({
  selector: "kpi-comment-form",
  templateUrl: "kpi-comment-form.component.html",
  styleUrls: ["kpi-comment-form.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCommentFormComponent implements OnInit {
  public kpi: Kpi;
  public snapshots: KpiSnapshot[];
  public projections: KpiProjection[];
  public comments: Comment[];
  public onCommentCreated: (kpiId: string) => void;
  public kpiCommentForm: FormGroup<KpiCommentForm>;
  public selectedPointValue: number;
  public isCreatingComment: boolean;
  public showTodaySnapshotWarning = false;

  private existingPointsWithoutCommentsDateValueMaps = new Map<KpiCommentTargetType, Map<string, number>>([
    ["kpi_snapshot", new Map<string, number>()],
    ["kpi_projection", new Map<string, number>()],
  ]);

  constructor(
    private formBuilder: FormBuilder,
    private commentsFacade: CommentsFacade,
    private stateService: StateService,
    private uiErrorHandlingService: UIErrorHandlingService,
    @Inject(UI_MODAL_DATA) modalData: KpiCommentFormModalData,
    private initialMentionsService: InitialMentionsService
  ) {
    Object.assign(this, modalData);
  }

  public ngOnInit(): void {
    const commentDatesByTargetTypeMap = this.generateCommentDatesByTargetTypeMap();
    const snapshotsWithoutComments = this.snapshots.filter((snapshot) => !commentDatesByTargetTypeMap.get("kpi_snapshot").has(snapshot.key));
    const projectionsWithoutComments = this.projections.filter((projection) => !commentDatesByTargetTypeMap.get("kpi_projection").has(projection.date));
    this.processPointsWithoutCommentsDateValueMaps(snapshotsWithoutComments, projectionsWithoutComments);
    this.initFormAndMetaData(snapshotsWithoutComments, projectionsWithoutComments);
    this.subscribeToFormChanges();
  }

  public isDateDisabled(date: Date): boolean {
    return !this.existingPointsWithoutCommentsDateValueMaps.get(this.kpiCommentForm.controls.targetType.value).has(dayjs(date).format(CHART_POINT_DATE_FORMAT));
  }

  public createComment(): Promise<Comment> {
    if (this.kpiCommentForm.invalid) {
      return Promise.reject();
    }

    this.isCreatingComment = true;

    return firstValueFrom(this.postComment$)
      .then(
        (newComment) => {
          this.onCommentCreated(this.kpi.id);
          this.stateService.go("^.comment", { commentId: newComment.id });

          return newComment;
        },
        (error) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          return Promise.reject();
        }
      )
      .finally(() => (this.isCreatingComment = false));
  }

  private get postComment$(): Observable<Comment> {
    const { id: targetId, name: targetTitle } = this.kpi;
    const { targetType: targetTypeControl, targetDate: targetDateControl, comment: commentControl } = this.kpiCommentForm.controls;
    const { note: text, gif, mentions } = commentControl.value;
    const newComment: CreateCommentDTO = {
      targetId,
      targetTitle,
      targetType: targetTypeControl.value,
      targetDate: dayjs(targetDateControl.value).format(CHART_POINT_DATE_FORMAT),
      text,
      gif,
      mentioned: [...new Set(mentions.removable.concat(mentions.nonRemovable))],
    };

    return this.commentsFacade.postComment$(newComment, { type: "comment", from: "button", numberOfReplies: "0" }).pipe(take(1));
  }

  private dateValidator = (control: FormControl<Date>): ValidationErrors => {
    if (this.isDateDisabled(control.value)) {
      return { dateDisabled: true };
    }

    return null;
  };

  private commentValidator = (control: FormControl<NoteWithGifAndMentions>): ValidationErrors => {
    if (!control.value.note && !control.value.gif) {
      return { commentRequired: true };
    }

    return null;
  };

  private getSnapshotValue(snapshotKey: string): number {
    return this.existingPointsWithoutCommentsDateValueMaps.get("kpi_snapshot").get(snapshotKey);
  }

  private getProjectionValue(projectionDate: string): number {
    return this.existingPointsWithoutCommentsDateValueMaps.get("kpi_projection").get(projectionDate);
  }

  private generateCommentDatesByTargetTypeMap(): Map<KpiCommentTargetType, Set<string>> {
    return this.comments.reduce(
      (commentDatesByType, comment) => {
        const commentDate = dayjs(comment.targetDate).format(CHART_POINT_DATE_FORMAT);
        commentDatesByType.get(<KpiCommentTargetType>comment.targetType).add(commentDate);
        return commentDatesByType;
      },
      new Map<KpiCommentTargetType, Set<string>>([
        ["kpi_snapshot", new Set<string>()],
        ["kpi_projection", new Set<string>()],
      ])
    );
  }

  private processPointsWithoutCommentsDateValueMaps(snapshotsWithoutComments: KpiSnapshot[], projectionsWithoutComments: KpiProjection[]): void {
    snapshotsWithoutComments.forEach((snapshot) => {
      this.existingPointsWithoutCommentsDateValueMaps.get("kpi_snapshot").set(snapshot.key, snapshot.value);
    });
    projectionsWithoutComments.forEach((projection) => {
      this.existingPointsWithoutCommentsDateValueMaps.get("kpi_projection").set(projection.date, projection.value);
    });
  }

  private generateFormTargetTypeAndDateInitialValues(
    snapshotsWithoutComments: KpiSnapshot[],
    projectionsWithoutComments: KpiProjection[]
  ): { targetType: KpiCommentTargetType; targetDate: string } {
    const [latestSnapshot, latestProjection] = [
      snapshotsWithoutComments[snapshotsWithoutComments.length - 1],
      projectionsWithoutComments[projectionsWithoutComments.length - 1],
    ];
    if (latestSnapshot) {
      return { targetType: "kpi_snapshot", targetDate: latestSnapshot.key };
    } else if (latestProjection) {
      return { targetType: "kpi_projection", targetDate: latestProjection.date };
    } else {
      return { targetType: "kpi_snapshot", targetDate: dayjs().format(CHART_POINT_DATE_FORMAT) };
    }
  }

  private transformKpiDataPointDateStringToSameDayDate(dateString: string): Date {
    const [year, month, day] = dateString.split("-").map((x) => parseInt(x));
    const monthIndex = month - 1;

    return new Date(year, monthIndex, day);
  }

  private setUpSelectedPointValue(targetType: KpiCommentTargetType, targetDate: string): void {
    if (targetType === "kpi_snapshot") {
      this.selectedPointValue = this.getSnapshotValue(targetDate);
    } else {
      this.selectedPointValue = this.getProjectionValue(targetDate);
    }
  }

  private markTargetDateAsDirtyAndUpdateValidity(): void {
    this.kpiCommentForm.controls.targetDate.markAsDirty();
    this.kpiCommentForm.controls.targetDate.updateValueAndValidity({ emitEvent: false });
  }

  private highlightInvalidDateIfNoPointsCanBeCommented(): void {
    const thereAreNoPointsWithoutComments = this.selectedPointValue === undefined;
    if (thereAreNoPointsWithoutComments) {
      this.markTargetDateAsDirtyAndUpdateValidity();
    }
  }

  private initFormAndMetaData(snapshotsWithoutComments: KpiSnapshot[], projectionsWithoutComments: KpiProjection[]): void {
    const { targetDate, targetType } = this.generateFormTargetTypeAndDateInitialValues(snapshotsWithoutComments, projectionsWithoutComments);
    this.kpiCommentForm = this.formBuilder.group<KpiCommentForm>({
      targetType: new FormControl(targetType, Validators.required),
      targetDate: new FormControl(this.transformKpiDataPointDateStringToSameDayDate(targetDate), [Validators.required]),
      comment: new FormControl<NoteWithGifAndMentions>(
        {
          note: null,
          gif: null,
          mentions: {
            removable: this.initRemovableMentions(),
            nonRemovable: [],
          },
        },
        [this.commentValidator]
      ),
    });
    this.kpiCommentForm.controls.targetDate.addValidators(this.dateValidator);
    this.setUpSelectedPointValue(targetType, targetDate);
    this.highlightInvalidDateIfNoPointsCanBeCommented();
    this.showTodaySnapshotWarning = shouldShowInfoIcon(targetType, targetDate);
  }

  private initRemovableMentions(): string[] {
    return this.initialMentionsService.getRemovableMentions({
      initialMentions: this.kpi.ownerIds,
      comments: [],
    });
  }

  private subscribeToFormChanges(): void {
    this.kpiCommentForm.controls.targetType.valueChanges.pipe(untilDestroyed(this)).subscribe((targetType) => {
      const targetDate = dayjs(this.kpiCommentForm.controls.targetDate.value).format(CHART_POINT_DATE_FORMAT);
      this.setUpSelectedPointValue(targetType, targetDate);
      this.showTodaySnapshotWarning = shouldShowInfoIcon(targetType, targetDate);
      this.markTargetDateAsDirtyAndUpdateValidity();
    });

    this.kpiCommentForm.controls.targetDate.valueChanges.pipe(untilDestroyed(this)).subscribe((newTargetDate) => {
      const targetType = this.kpiCommentForm.controls.targetType.value;
      const targetDate = dayjs(newTargetDate).format(CHART_POINT_DATE_FORMAT);
      this.setUpSelectedPointValue(targetType, targetDate);
      this.showTodaySnapshotWarning = shouldShowInfoIcon(this.kpiCommentForm.controls.targetType.value, targetDate);
    });
  }
}
